import { useRef, useState } from 'react';
import YouTube from 'react-youtube';
import SwiperCore, { Mousewheel, Pagination, Thumbs } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import styled, { Box, css, th } from '@xstyled/styled-components';
import { useUpdateAtom } from 'jotai/utils';
import { currentGalleryIndexAtom, lightboxAtom } from 'global/store';
import Gallery from './Gallery';
import Image from '@/UI/Image';

SwiperCore.use([Mousewheel, Pagination, Thumbs]);

const GalleryContainer = styled.div`
  display: flex;
  max-width: 520px;
  padding-top: 3rem;

  .swiper-slide {
    cursor: pointer;
  }

  @media (max-width: desktop) {
    display: none;
  }
`;

const MainThumb = styled(Swiper)`
  flex: 1;
  max-height: 400px;
  margin: -2rem 0 0 5rem;
  padding-bottom: 1.5rem;

  .swiper-pagination {
    bottom: 0;
  }

  .swiper-pagination-bullet-active {
    background-color: teal-300;
  }
`;
const VideoContainer = styled.div`
  margin-top: 2.5rem;
  align-items: center;
  justify-content: center;
`;

const SmallThumbs = styled(Swiper)`
  max-height: 290px;

  .swiper-slide-active {
    border: 0.7px solid ${th.color('purple-100')};
    border-radius: 5px;
  }

  ${({ hide }) =>
    hide &&
    css`
      display: none;
    `}
`;

export default function ImageSlider({ images, product }) {
  const sliderRef = useRef(null);
  const [thumbsSlider, setThumbsSlider] = useState(null);

  const setCurrentIndex = useUpdateAtom(currentGalleryIndexAtom);
  const setLightboxOpen = useUpdateAtom(lightboxAtom);

  return (
    <>
      <GalleryContainer>
        <SmallThumbs
          ref={sliderRef}
          onSwiper={setThumbsSlider}
          direction="vertical"
          slidesPerView={3}
          spaceBetween={16}
          loop={images?.length > 3 && true}
          hide={images?.length === 1 ? 1 : null}
          mousewheel={{ sensitivity: 500 }}
          watchSlidesProgress
        >
          {images?.map((item, i) => {
            return (
              <SwiperSlide key={item.url}>
                <Box
                  w={80}
                  p={8}
                  onClick={() => {
                    setCurrentIndex(i);
                    sliderRef.current.swiper.slideToLoop(i);
                  }}
                >
                  <Image
                    src={item.url}
                    alt={item.label || product}
                    width={80}
                    height={80}
                  />
                </Box>
              </SwiperSlide>
            );
          })}
        </SmallThumbs>

        {images?.length > 1 ? (
          <MainThumb
            thumbs={{ swiper: thumbsSlider }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 3
            }}
            loop={true}
          >
            {images.map((item, i) => {
              return (
                <SwiperSlide
                  key={item.url}
                  onClick={() => {
                    setCurrentIndex(i);
                    setLightboxOpen(true);
                  }}
                >
                  {item.video_content ? (
                    <VideoContainer>
                      <YouTube
                        videoId={item.video_content.video_url.split('v=')[1]}
                        opts={{
                          height: '290px',
                          width: '100%',
                          borderRadius: '4px',
                          autoplay: 0,
                          playerVars: {
                            autoplay: 0
                          }
                        }}
                      />
                    </VideoContainer>
                  ) : (
                    <Image
                      src={item.url}
                      alt={item.label || product}
                      width={360}
                      height={360}
                    />
                  )}
                </SwiperSlide>
              );
            })}
          </MainThumb>
        ) : (
          <Box ml={112} cursor="pointer" onClick={() => setLightboxOpen(true)}>
            {images?.map(item => {
              return (
                <Image
                  key={item.url}
                  src={item.url}
                  alt={item.label || product}
                  width={360}
                  height={360}
                />
              );
            })}
          </Box>
        )}
      </GalleryContainer>

      {/* Lightbox */}
      <Gallery images={images} />
    </>
  );
}
